/**
 * useDocumentContent — the in-app editor's body content.
 *
 * GET /api/v1/documents/{id}/content  → { content_html, updated_at_utc }
 * PUT /api/v1/documents/{id}/content  → save the edited body.
 *
 * Served by the mock-api via the prod-proxy 404-fallback (the real document-svc
 * has no /content path). This backs the always-available in-app editor, so
 * editing works regardless of the OnlyOffice Document Server's wiring.
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export interface DocumentContent {
  document_id: string;
  content_html: string;
  updated_at_utc: string | null;
  updated_by: string | null;
}

export function useDocumentContentQuery(documentId: string | undefined): UseQueryResult<DocumentContent> {
  return useQuery<DocumentContent>({
    queryKey: ['documents', documentId, 'content'],
    queryFn: ({ signal }) =>
      apiFetch<DocumentContent>(`/api/v1/documents/${encodeURIComponent(documentId!)}/content`, { signal }),
    enabled: !!documentId,
    staleTime: 5_000,
  });
}

export function useSaveContentMutation(documentId: string | undefined): UseMutationResult<DocumentContent, Error, string> {
  const qc = useQueryClient();
  return useMutation<DocumentContent, Error, string>({
    mutationFn: (content_html: string) =>
      apiFetch<DocumentContent>(`/api/v1/documents/${encodeURIComponent(documentId!)}/content`, {
        method: 'PUT',
        body: { content_html },
      }),
    onSuccess: (data) => qc.setQueryData(['documents', documentId, 'content'], data),
  });
}
